import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { GoogleSignInButton } from "../components/GoogleSignInButton";
import { OneTapSignIn } from "../components/OneTapSignIn";

interface AddressInfo {
  street: string;
  city: string;
  province: string;
}

function SignupPage() {
  const [step, setStep] = useState<"form" | "verify">("form");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Form data
  const [formData, setFormData] = useState({
    email: "",
  });
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [testOtp, setTestOtp] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user types
    setError("");
  };

  const startSignup = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email) {
      setError("Please enter your email address");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const API_BASE = import.meta.env.DEV
        ? "http://localhost:8787"
        : "https://shopglobal.finhub.workers.dev";

      const response = await fetch(`${API_BASE}/api/auth/signup/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formData.email,
        }),
      });

      const result = await response.json();

      if (result.success) {
        // Set test OTP for development (show in UI)
        setTestOtp(result.testOtp || null);
        setStep("verify");
      } else {
        const errorMsg = result.error || "Failed to send verification code";

        // If account exists, transparently redirect to login
        if (errorMsg.includes("Account already exists")) {
          // Automatically redirect to login page
          navigate("/login");
          return; // Don't continue processing
        } else {
          setError(errorMsg);
        }
      }
    } catch (error) {
      console.error("Signup start error:", error);
      setError("Failed to send verification code. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const completeSignup = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!otp) {
      setError("Please enter the verification code");
      return;
    }

    if (otp.length !== 6) {
      setError("Please enter a valid 6-digit verification code");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const API_BASE = import.meta.env.DEV
        ? "http://localhost:8787"
        : "https://shopglobal.finhub.workers.dev";

      const response = await fetch(`${API_BASE}/api/auth/signup/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formData.email,
          otp: otp,
        }),
      });

      const result = await response.json();

      if (result.success) {
        // Store session and user data, then redirect to onboarding for new users
        localStorage.setItem("shopglobal_auth_token", result.session.token);

        // Also store user data for immediate access (basic user object)
        if (result.user) {
          const userData = {
            id: result.user.id,
            email: result.user.email,
            fullName: result.user.fullName,
            membershipTier: result.user.membershipTier || "free",
            delawareAddress: result.user.delawareAddress || "",
            canadianAddress: {
              street: "",
              city: "",
              province: "",
              postalCode: "",
            },
            verified: true,
            role: "user",
            createdAt: new Date().toISOString(),
          };
          localStorage.setItem("shopglobal_user", JSON.stringify(userData));
        }

        console.log("✅ Signup successful, redirecting to onboarding...");
        navigate("/onboarding");
      } else {
        setError(result.error || "Verification failed");
      }
    } catch (error) {
      console.error("Signup complete error:", error);
      setError("Failed to complete signup. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-8">
      <div className="max-w-lg w-full bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold mx-auto mb-4">
            SG
          </div>
          <h2 className="text-2xl font-bold">Create Your Account</h2>
          <p className="text-gray-600">Start receiving packages from the US</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 text-sm">
            {error}
          </div>
        )}

        {message && (
          <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg mb-6 text-sm">
            <div dangerouslySetInnerHTML={{ __html: message }}></div>
          </div>
        )}

        {step === "form" ? (
          <form onSubmit={startSignup} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="john@example.com"
                required
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
              {isLoading ? "Sending Code..." : "Send Verification Code"}
            </button>
          </form>
        ) : (
          <form onSubmit={completeSignup} className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <div className="text-green-800 text-sm">
                ✅ Verification code sent to <strong>{formData.email}</strong>
              </div>
              <div className="text-green-600 text-xs mt-1">
                Check your email (including spam folder) and enter the 6-digit
                code.
              </div>
              {testOtp && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 mt-3">
                  <div className="text-yellow-800 text-xs font-mono text-center">
                    🔧 TEST CODE: <span className="font-bold">{testOtp}</span>
                    <br />
                    <span className="text-yellow-600">
                      (Remove this in production)
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Verification Code
              </label>
              <input
                type="text"
                value={otp}
                onChange={(e) =>
                  setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-center text-2xl font-mono tracking-wider"
                placeholder="000000"
                maxLength={6}
                required
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
              {isLoading ? "Verifying..." : "Verify & Create Account"}
            </button>

            <button
              type="button"
              onClick={() => setStep("form")}
              className="w-full text-blue-600 hover:text-blue-700 text-sm font-medium">
              ← Change Email or Details
            </button>
          </form>
        )}

        {/* Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">
              Or continue with
            </span>
          </div>
        </div>

        {/* Google Sign In Button */}
        <GoogleSignInButton
          onSuccess={() => {
            console.log(
              "✅ Google Sign In successful, redirecting to onboarding..."
            );
            navigate("/onboarding");
          }}
          onError={(error) => {
            console.error("❌ Google Sign In error:", error);
            setError(error);
          }}
          className="w-full"
        />

        {/* One-Tap Sign In Button */}
        <div className="mt-4">
          <OneTapSignIn
            onSuccess={() => {
              console.log(
                "✅ One-Tap Sign In successful, redirecting to onboarding..."
              );
              navigate("/onboarding");
            }}
            onError={(error) => {
              console.error("❌ One-Tap Sign In error:", error);
              setError(error);
            }}
            className="w-full"
          />
        </div>

        <p className="text-center text-sm text-gray-600 mt-6">
          Already have an account?{" "}
          <Link to="/login" className="text-blue-600 hover:text-blue-700">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}

export default SignupPage;
